/**
 * Bottom-right ammo + weapon block.
 *
 * One canvas so the hand-drawn numerals, the reload arc and the weapon plate
 * share the same rasteriser and the same dark halo. Redrawn only while a value
 * is animating, otherwise it sits idle at zero cost.
 */
import { COL, clamp01, ease, rgba, fitCanvas, clearCanvas, devicePx, crispRect } from './Theme.js';
import { drawGlyphs, measureGlyphs, label, measureLabel, icon, ICON } from './Glyphs.js';

export const W = 348, H = 150;

const TAU = Math.PI * 2;
const BASE = 122;    // numeral baseline
const MAG_H = 60;    // magazine numeral cap height
const RES_H = 24;    // reserve numeral cap height

export class AmmoBlock {
  constructor(root) {
    this.cv = document.createElement('canvas');
    this.cv.className = 'hud-c hud-ammo';
    root.appendChild(this.cv);
    this.g = fitCanvas(this.cv, W, H);
    this.dirty = true;

    this.magPunch = 0;      // 0..1, decays — drives the counter snap
    this.magPunchDir = 0;   // -1 spend, +1 gain
    this.swapT = 1;         // 0..1 weapon-switch slide-in
  }

  resize(dpr = devicePx()) {
    this.g = fitCanvas(this.cv, W, H, dpr);
    this.dirty = true;
  }

  punch(dir) { this.magPunch = 1; this.magPunchDir = dir; this.dirty = true; }
  swap() { this.swapT = 0; this.dirty = true; }

  advance(dt) {
    if (this.magPunch > 0) { this.magPunch = Math.max(0, this.magPunch - dt * 6.2); this.dirty = true; }
    if (this.swapT < 1) { this.swapT = Math.min(1, this.swapT + dt * 3.6); this.dirty = true; }
  }

  draw(s) {
    const g = this.g;
    clearCanvas(g);

    const slide = ease.outQuint(clamp01(this.swapT));
    g.save();
    g.globalAlpha = 0.12 + slide * 0.88;
    g.translate((1 - slide) * 30, 0);

    const magMax = Math.max(1, s.magMax);
    const frac = clamp01(s.mag / magMax);
    const low = s.mag <= Math.max(1, Math.ceil(magMax * 0.30));
    const crit = s.mag <= Math.max(1, Math.ceil(magMax * 0.12));
    const empty = s.mag <= 0;
    const pulse = crit ? 0.74 + 0.26 * Math.sin(s.time * 7.4) : 1;
    const magCol = crit || empty ? COL.enemy : low ? COL.amber : COL.white;

    /* --------------------------------------------------- weapon name row */
    const nameW = measureLabel(g, s.weaponName, { size: 15, weight: 700, tracking: 0.21 });
    label(g, s.weaponName, W - 2, 4, {
      size: 15, weight: 700, tracking: 0.21, align: 'right', color: COL.white,
    });
    // Hair rule running out from under the name — the classic COD "tab".
    g.fillStyle = rgba(COL.white, 0.16);
    crispRect(g, 84, 26, W - 86, 1);
    g.fillStyle = COL.amber;
    crispRect(g, W - 2 - Math.min(nameW, 200), 26, Math.min(nameW, 200), 1);

    label(g, s.attachment || '', W - 2, 33, {
      size: 10, weight: 600, tracking: 0.22, align: 'right', color: rgba(COL.dim, 0.85),
    });

    /* ------------------------------------------------------- ammo numbers */
    const reserveStr = String(Math.max(0, Math.round(s.reserve)));
    const magStr = String(Math.max(0, Math.round(s.mag)));
    const resW = measureGlyphs(reserveStr, RES_H, 0.10);

    // reserve, sitting on the same baseline as the magazine
    drawGlyphs(g, reserveStr, W - 2, BASE - RES_H, RES_H, {
      align: 'right', color: rgba(COL.bone, 0.68), weight: 0.18, tracking: 0.10,
    });
    // Separator: an explicit chamfered diagonal, not a glyph, so its weight is
    // tuned against the magazine numeral rather than inherited from the font.
    const sepX = W - 8 - resW;
    g.lineCap = 'butt';
    g.lineWidth = 5;
    g.strokeStyle = rgba('#000000', 0.55);
    g.beginPath(); g.moveTo(sepX - 1, BASE + 1); g.lineTo(sepX - 15, BASE - MAG_H + 4); g.stroke();
    g.lineWidth = 3;
    g.strokeStyle = rgba(COL.bone, 0.42);
    g.beginPath(); g.moveTo(sepX - 1, BASE); g.lineTo(sepX - 15, BASE - MAG_H + 5); g.stroke();

    // Magazine — the hero number. Punches about its own baseline-right anchor.
    const pk = this.magPunch;
    const scale = pk > 0 ? 1 + (1 - ease.outBack(1 - pk)) * 0.15 : 1;
    const magRight = sepX - 20;
    g.save();
    g.translate(magRight, BASE);
    g.scale(scale, scale);
    g.translate(-magRight, -BASE);
    drawGlyphs(g, magStr, magRight, BASE - MAG_H, MAG_H, {
      align: 'right', color: rgba(magCol, pulse), weight: 0.155, tracking: 0.06,
      haloWidth: 2.4,
    });
    if (pk > 0.55) {
      // brief bright rim on the frame the value changed
      drawGlyphs(g, magStr, magRight, BASE - MAG_H, MAG_H, {
        align: 'right', color: rgba('#FFFFFF', (pk - 0.55) * 2.2), weight: 0.05,
        tracking: 0.06, halo: null,
      });
    }
    g.restore();

    /* ------------------------------------------------------- magazine ring */
    drawRing(g, 40, 84, 27, s, frac, magCol);

    /* ------------------------------------------------- fire mode + status */
    const fmY = 130;
    const fmW = label(g, s.fireMode, W - 2, fmY, {
      size: 12, weight: 700, tracking: 0.22, align: 'right', color: rgba(COL.bone, 0.85),
    });
    // fire-mode pips: SEMI(1) / BURST(2) / AUTO(3)
    const pips = s.fireMode === 'AUTO' ? 3 : s.fireMode === 'BURST' ? 2 : 1;
    for (let i = 0; i < 3; i++) {
      g.fillStyle = i < pips ? COL.amber : rgba(COL.white, 0.16);
      crispRect(g, W - 12 - fmW - 22 + i * 7, fmY + 2, 4, 9);
    }

    if (s.reloading) {
      label(g, 'RELOADING', 78, fmY, { size: 12, weight: 700, tracking: 0.26, color: COL.amber });
    } else if (empty) {
      label(g, 'RELOAD  [R]', 78, fmY, {
        size: 12, weight: 700, tracking: 0.26, color: rgba(COL.enemy, pulse),
      });
    }

    g.restore();
    this.dirty = this.magPunch > 0 || this.swapT < 1 || s.reloading || crit || empty;
  }
}

function drawRing(g, cx, cy, r, s, frac, magCol) {
  const start = -Math.PI / 2;

  // Backing disc so the ring never fights a bright background.
  g.beginPath();
  g.arc(cx, cy, r + 7, 0, TAU);
  g.fillStyle = rgba('#04070A', 0.42);
  g.fill();

  // track
  g.lineCap = 'butt';
  g.lineWidth = 5;
  g.strokeStyle = rgba('#000000', 0.55);
  g.beginPath(); g.arc(cx, cy, r, 0, TAU); g.stroke();
  g.lineWidth = 3;
  g.strokeStyle = rgba(COL.white, 0.16);
  g.beginPath(); g.arc(cx, cy, r, 0, TAU); g.stroke();

  if (s.reloading) {
    const p = clamp01(s.reloadT / Math.max(0.01, s.reloadDur));
    g.lineWidth = 7;
    g.strokeStyle = rgba('#000000', 0.6);
    g.beginPath(); g.arc(cx, cy, r, start, start + TAU * p); g.stroke();
    g.lineWidth = 5;
    g.strokeStyle = COL.amber;
    g.beginPath(); g.arc(cx, cy, r, start, start + TAU * p); g.stroke();
    // leading tick
    const a = start + TAU * p;
    g.save();
    g.translate(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
    g.rotate(a);
    g.fillStyle = 'rgba(0,0,0,0.6)';
    g.fillRect(-2.5, -7, 5, 14);
    g.fillStyle = '#FFF3D8';
    g.fillRect(-1.5, -6, 3, 12);
    g.restore();
    drawGlyphs(g, String(Math.round(p * 100)), cx, cy - 10, 20, {
      align: 'center', color: COL.amber, weight: 0.19, tracking: 0.08,
    });
  } else {
    // Idle: the ring quietly mirrors magazine fill.
    g.lineWidth = 6;
    g.strokeStyle = rgba('#000000', 0.55);
    g.beginPath(); g.arc(cx, cy, r, start, start + TAU * frac); g.stroke();
    g.lineWidth = 4;
    g.strokeStyle = rgba(magCol, 0.92);
    g.beginPath(); g.arc(cx, cy, r, start, start + TAU * frac); g.stroke();
    icon(ICON.magazine, g, cx - 11, cy - 15, 22, 30, rgba(COL.bone, 0.92));
  }
}
